import { trans } from "@mongez/localization";
import { ReactAtom } from "@mongez/react-atom";
import { useSearchParams } from "@remix-run/react";
import { PaginationInfo, parseError } from "core/shared";
import { action } from "framework/config/action";
import { useState } from "react";
import { errorToast, toastLoading } from "~/design-system/components";
import { heavyObject } from "../utils";

export type ItemsAtom = {
  [key: string]: any;
  isLoadingMore: boolean;
  paginationInfo: PaginationInfo;
};

type Props = {
  actionUrl: string;
  itemsAtom: ReactAtom<ItemsAtom>;
  itemsAtomKey: string;
  onLoadMore?: () => void;
};

type GetFilterParamsProps = {
  params?: any;
};

function getFilterParams({ params }: GetFilterParamsProps) {
  return heavyObject({
    ...params,
  });
}

/**
 * A custom React hook that handles infinite scrolling or "Load More" functionality
 * by managing pagination state and data fetching.
 *
 * @example
 * ```tsx
 * // In your component:
 * const ordersAtom = useAtom({
 *   key: "orders",
 *   default: {
 *     orders: [],
 *     isLoadingMore: false,
 *     paginationInfo: { page: 1, pages: 1 },
 *     defaultParams: { limit: 10 }
 *   }
 * });
 *
 * const { loadMoreProducts, hasNextPage } = useLoadMore({
 *   actionUrl: "/orders",
 *   itemsAtom: ordersAtom,
 *   itemsAtomKey: "orders",
 *   onLoadMore: () => console.log("More products loaded")
 * });
 *
 * // In your JSX:
 * const orders = ordersAtom.use("orders");
 *
 * <OrdersList orders={orders} />
 * {hasNextPage && (
 *   <LoadMore
 *     itemsAtom={ordersAtom as any}
 *     itemsAtomKey="orders"
 *     actionUrl={URLS.account.orders}
 *   />
 * )}
 * ```
 *
 * @param {Object} props - The hook configuration object
 * @param {string} props.actionUrl - The Remix action route URL to fetch more items from
 * @param {ReactAtom<ItemsAtom>} props.itemsAtom - A React Atom instance that holds the items state
 * @param {string} props.itemsAtomKey - The key in the atom where the items array is stored
 * @param {Function} [props.onLoadMore] - Optional callback function to be called after loading more items
 * @returns {Object} An object containing:
 *   - loadMoreProducts: Function to trigger loading more items
 *   - hasNextPage: Boolean indicating if there are more pages to load
 */
export function useLoadMore({
  actionUrl,
  itemsAtom,
  itemsAtomKey,
  onLoadMore,
}: Props): {
  loadMore: () => void;
  isLoading: boolean;
  hasNextPage: boolean;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const submit = action(actionUrl);

  const loadMore = async () => {
    // Get URL search params as object, handling both single values and arrays
    const urlParams: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      if (key.endsWith("[]")) {
        const baseKey = key.replace("[]", "");
        urlParams[baseKey] = searchParams.getAll(key);
      } else {
        // Skip if this key has an array variant (e.g., if there's a key with "[]" suffix)
        if (!(key in urlParams)) {
          urlParams[key] = value;
        }
      }
    });

    const values = getFilterParams({
      params: {
        page: itemsAtom.get("paginationInfo")?.page + 1,
        ...itemsAtom.get("params"),
        ...urlParams,
        limit: 12,
      },
    });

    // Please note on the load more behavior, we don't update the query string,
    // because we want to keep the current query string, and only update the page number,
    // this will prevent the page from being reset to the first page when the user clicks on the load more button.
    const loader = toastLoading(trans("loading"));
    setIsLoading(true);

    itemsAtom.merge({
      isLoadingMore: true,
    });

    try {
      const response = await submit(values);

      if (response.error) {
        errorToast(parseError(response));
      }

      onLoadMore?.();

      itemsAtom.merge({
        isLoadingMore: false,
        [itemsAtomKey]: [
          ...itemsAtom.get(itemsAtomKey),
          ...response.data[itemsAtomKey],
        ],
        paginationInfo: response.data.paginationInfo,
      });
    } catch (error) {
      errorToast(parseError(error));
    } finally {
      loader.dismiss();
      setIsLoading(false);
      itemsAtom.merge({
        isLoadingMore: false,
      });
    }
  };

  return {
    loadMore,
    isLoading,
    get hasNextPage() {
      return (
        itemsAtom.get("paginationInfo")?.pages >
        itemsAtom.get("paginationInfo")?.page
      );
    },
  };
}
